import { createShape, type DesignCanvasSettings, type DesignConnectorShape, type DesignShape } from '../../../../types/design'

export type DesignPayloadNode = {
  id: string
  type: string
  x: number
  y: number
  label: string
}

export type DesignPayloadConnector = {
  from: string
  to: string
}

export type DesignPayload = Record<string, unknown> & {
  canvas?: Partial<DesignCanvasSettings>
  lanes?: string[]
  nodes: DesignPayloadNode[]
  connectors: DesignPayloadConnector[]
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? value as Record<string, unknown> : {}

const asString = (value: unknown, fallback = '') => typeof value === 'string' ? value : fallback
const asNumber = (value: unknown, fallback = 0) => {
  const next = Number(value)
  return Number.isFinite(next) ? next : fallback
}

const asStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(String).map((item) => item.trim()).filter(Boolean) : []

export const normalizeDesignPayload = (value: unknown): DesignPayload => {
  const payload = asRecord(value)
  const next: DesignPayload = {
    ...payload,
    nodes: Array.isArray(payload.nodes)
      ? payload.nodes.map((node, index) => {
        const record = asRecord(node)
        return {
          id: asString(record.id, `node-${index + 1}`),
          type: asString(record.type, 'stage'),
          x: asNumber(record.x),
          y: asNumber(record.y),
          label: asString(record.label, `Node ${index + 1}`),
        }
      })
      : [],
    connectors: Array.isArray(payload.connectors)
      ? payload.connectors.map((connector) => {
        const record = asRecord(connector)
        return {
          from: asString(record.from),
          to: asString(record.to),
        }
      }).filter((connector) => connector.from && connector.to)
      : [],
  }
  if (payload.canvas && typeof payload.canvas === 'object' && !Array.isArray(payload.canvas)) next.canvas = payload.canvas as Partial<DesignCanvasSettings>
  const lanes = asStringList(payload.lanes)
  if (lanes.length > 0) next.lanes = lanes
  return next
}

export const designPayloadToShapes = (payload: DesignPayload): DesignShape[] => {
  const shapes: DesignShape[] = payload.nodes.map((node) => {
    const shape = createShape(node.type === 'decision' ? 'DIAMOND' : 'PROCESS', { x: node.x, y: node.y }, node.type === 'decision' ? { width: 160, height: 96 } : { width: 180, height: 64 }, node.id)
    return { ...shape, text: node.label } as DesignShape
  })
  payload.connectors.forEach((connector, index) => {
    if (!connector.from || !connector.to) return
    const shape = createShape('CONNECTOR', { x: 0, y: 0 }, { width: 1, height: 1 }, `connector-${index + 1}`) as DesignConnectorShape
    shapes.push({
      ...shape,
      from: { kind: 'SHAPE', shapeId: connector.from, port: 'AUTO' },
      to: { kind: 'SHAPE', shapeId: connector.to, port: 'AUTO' },
    })
  })
  return shapes
}
